import { PaycheckRoutingStatus } from '@fruitful/income-split';
import {
    CashBackPolicy,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { router } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
    ArrowLeftRightIcon,
    BanknoteArrowDownIcon,
    BodyLargeRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CirclePlusIcon,
    EllipsisIcon,
    FileTextIcon,
    H1Emphasized,
    Icon,
    IdCard,
    InfoButton,
    Private,
    RefreshCwIcon,
    RowList,
    ScanLineIcon,
    Sheet,
    SmoothieScrollView,
    SplitIcon,
    SuperscriptText,
    TileButton,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useUserAuthorization } from '../../../auth/hooks/useUserAuthorization';
import {
    CardFeatureCardGraphic,
    FeatureCard,
} from '../../../common/components/FeatureCard';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import {
    BankAccountCreationDisabledReason,
    canUserCreateBankAccount,
    sortBankAccounts,
} from '../../../common/utils/bankingUtils';
import { invertNormalizedValue } from '../../../common/utils/cardUtils';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import PendingJointInvitationsNotice from '../../../home/components/joint-invitations/PendingJointInvitationsNotice';
import { captureWarning } from '../../../logger/Logger';
import { MoneyMapEarningsTileForDirectDeposit } from '../../../roadmap/components/MoneyMapEarnings';
import { useRoadmapTabExperience } from '../../../roadmap/hooks/useRoadmapTabExperience';
import useGetIncomeSplitFromGlobalState from '../../hooks/income-split/useGetIncomeSplitFromGlobalState';
import useGetIncomeSplitFromGlobalStateNew from '../../hooks/income-split/useGetIncomeSplitFromGlobalStateNew';
import useShouldSeeIncomeSplit from '../../hooks/income-split/useShouldSeeIncomeSplit';
import useGetExistingPaycheckConfigs from '../../hooks/paycheck-routing/useGetPaycheckConfigs';
import useGetAllPaychecks from '../../hooks/paycheck-routing/useGetPaychecks';
import { existingPaycheckRoutingConfigsAtom } from '../../state/PaycheckRoutingAtoms';
import {
    bankAccountDisplayDetails,
    selectedAccountForDirectDepositAtom,
} from '../../state/SpacesAtoms';
import type { BankAccountDisplayDetail } from '../../types/BankAccount';
import { ProductFeatureDisclosures } from '../Disclosures';
import useGetExistingIncomeSplitConfigs from '../income-split/hooks/useGetIncomeSplitConfigs';
import { IncomeSplitButton } from '../income-split/IncomeSplitButton';
import PaycheckRoutingSetupWidget from '../paycheck-routing/PaycheckRoutingSetupWidget';
import InterestCashBackEarned from './InterestCashBackEarned';
import AccountLimitSheet from './sheets/AccountLimitSheet';
import AvailableBalanceInfoSheet from './sheets/AvailableBalanceInfoSheet';
import SpaceCard from './SpaceCard';

interface SpacesOverviewProps {
    totalCurrentBalance: string;
    totalAvailableBalance: string;
    totalCardBalance: string;
    totalPendingTransfers: string;
    totalPendingCardTransactions: string;
    totalInterestEarned: string;
    totalCashbackEarned: string;
    bankAccountDetails: BankAccountDisplayDetail[];
    isMoneyMapCompleted: boolean;
    moneyMap?: MoneyMapWithRulesAndAccounts;
}

const SpacesOverview = ({
    totalCurrentBalance,
    totalAvailableBalance,
    totalCardBalance,
    totalPendingTransfers,
    totalPendingCardTransactions,
    totalInterestEarned,
    totalCashbackEarned,
    bankAccountDetails,
    isMoneyMapCompleted,
    moneyMap,
}: SpacesOverviewProps) => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('Banking/SpacesOverview');

    const [isMoreButtonsSheetOpen, setIsMoreButtonsSheetOpen] = useState(false);
    const [
        isAvailableBalanceInfoSheetOpen,
        setIsAvailableBalanceInfoSheetOpen,
    ] = useState(false);
    const [isAccountLimitSheetOpen, setIsAccountLimitSheetOpen] =
        useState(false);

    const existingPaycheckRoutingConfigs = useAtomValue(
        existingPaycheckRoutingConfigsAtom,
    );

    const { incomeSplit: incomeSplitLegacy } =
        useGetIncomeSplitFromGlobalState();
    const { incomeSplit } = useGetIncomeSplitFromGlobalStateNew();

    const hasExistingIncomeSplitLegacy =
        (incomeSplitLegacy?.rules?.length ?? 0) > 0;
    const hasExistingIncomeSplit = (incomeSplit?.ruleSets?.length ?? 0) > 0;

    const incomeSplitIsActive = hasExistingIncomeSplit
        ? incomeSplit?.status === PaycheckRoutingStatus.ACTIVE
        : hasExistingIncomeSplitLegacy
          ? incomeSplitLegacy?.status === PaycheckRoutingStatus.ACTIVE
          : false;

    const setAccountForDirectDeposit = useSetAtom(
        selectedAccountForDirectDepositAtom,
    );

    const user = useAuthedUser();
    const { accessToRestrictedCapabilities } = useUserAuthorization();
    const toast = useToastController();
    const showCashBack = false; // TODO: Not showing cashback for initial member launch as of 10/01/2024
    const hasCards = bankAccountDetails.some((acc) => acc.hasCard);
    const showFruitfulCardFeatureCard =
        !hasCards &&
        user.membership?.product?.banking.cashBackPolicy ===
            CashBackPolicy.DD_REQUIREMENT;

    const isJointMembership = user.partnerMember != null;

    const shouldSeeIncomeSplit = useShouldSeeIncomeSplit();
    const accounts = useAtomValue(bankAccountDisplayDetails);
    const hasAtLeastTwoFruitfulAccounts = accounts.length > 1;
    const showPaycheckSplit =
        !shouldSeeIncomeSplit && hasAtLeastTwoFruitfulAccounts;

    // TODO: probably check loading state for these hooks and render something?
    const { detectedPaychecks, isLoading: isLoadingAllPaychecks } =
        useGetAllPaychecks();
    useGetExistingPaycheckConfigs();
    useGetExistingIncomeSplitConfigs();

    const sortedBankAccounts = useMemo(
        () => sortBankAccounts(bankAccountDetails, moneyMap),
        [bankAccountDetails, moneyMap],
    );

    const { incomeSplitConfigs: tiedRulesIncomeSplitConfigs } =
        useGetExistingIncomeSplitConfigs();

    const { value: enableIncomeSplitVisuals } = useFeatureGate(
        FeatureFlags.ENABLE_INCOME_SPLIT_VISUALS,
    );
    const routeToIncomeSplit = () => {
        enableIncomeSplitVisuals && tiedRulesIncomeSplitConfigs.length > 0
            ? router.navigate('/income-split/overview')
            : router.navigate('/income-split');
    };

    return (
        <SmoothieScrollView containerStyle={{ marginHorizontal: 24 }}>
            <YStack $sm={{ maxWidth: 700, alignSelf: 'center' }}>
                <YStack marginBottom={24} alignItems="center" gap={10}>
                    <Private>
                        <H1Emphasized>{totalAvailableBalance}</H1Emphasized>
                    </Private>
                    <XStack justifyContent="center">
                        <InfoButton
                            subtle
                            text="Total available"
                            onPress={() => {
                                setIsAvailableBalanceInfoSheetOpen(true);
                            }}
                        />
                    </XStack>
                    <InterestCashBackEarned
                        interestEarned={totalInterestEarned}
                        cashBackEarned={totalCashbackEarned}
                        showCashBack={showCashBack}
                    />
                    <MoneyMapEarningsTileForDirectDeposit
                        variant="simple"
                        moneyMap={moneyMap}
                        isMoneyMapCompleted={isMoneyMapCompleted}
                        appContext="banking-overview"
                    />
                </YStack>
                <XStack
                    justifyContent="space-between"
                    gap={8}
                    height={54}
                    marginBottom={12}
                >
                    <TileButton
                        icon={ArrowLeftRightIcon}
                        text="Transfer"
                        onPress={() => {
                            router.navigate('/banking/transfer');
                        }}
                    />
                    {accessToRestrictedCapabilities.openBankAccount && (
                        <TileButton
                            icon={CirclePlusIcon}
                            text="New account"
                            onPress={() => {
                                const { allowed, reason } =
                                    canUserCreateBankAccount(user);

                                if (!allowed) {
                                    if (
                                        reason ===
                                        BankAccountCreationDisabledReason.MAX_LIMIT
                                    ) {
                                        setIsAccountLimitSheetOpen(true);
                                    } else {
                                        toast.show(
                                            "Sorry, but you can't create a new account right now.",
                                        );
                                    }
                                } else {
                                    router.navigate(
                                        '/banking/onboarding/space-ownership',
                                    );
                                }
                            }}
                        />
                    )}
                    <TileButton
                        icon={EllipsisIcon}
                        text="More"
                        onPress={() => {
                            setIsMoreButtonsSheetOpen(true);
                        }}
                    />
                </XStack>

                <YStack gap={12}>
                    {!shouldSeeIncomeSplit && (
                        <BodyLargeRegular color="$gray1100">
                            Accounts
                        </BodyLargeRegular>
                    )}
                    <PendingJointInvitationsNotice isFullWidth={true} />
                    <RowList paddingVertical={4}>
                        {sortedBankAccounts.map((detail) => {
                            return (
                                <SpaceCard
                                    key={detail.bankAccountId}
                                    bankAccountDetail={detail}
                                    isJointMembership={isJointMembership}
                                    onPress={() => {
                                        router.navigate({
                                            pathname: '/banking/space-details',
                                            params: {
                                                syncteraAccountId:
                                                    detail.syncteraAccountId,
                                            },
                                        });
                                    }}
                                />
                            );
                        })}
                    </RowList>

                    <YStack marginTop={24} gap={16}>
                        {showFruitfulCardFeatureCard && (
                            <FeatureCard
                                name={
                                    <SuperscriptText
                                        textBefore="Get up to 2% cash back"
                                        superscript={1}
                                        onInvalidSuperscriptText={
                                            captureWarning
                                        }
                                    />
                                }
                                descriptionAsList={[
                                    'Open a Fruitful Cash Account',
                                    'Add a Fruitful Card',
                                    'Set up a direct deposit of $2,000 or more',
                                ]}
                                href="/banking/onboarding/space-ownership"
                                alt
                                graphic={<CardFeatureCardGraphic />}
                            />
                        )}
                        {!shouldSeeIncomeSplit &&
                            !isLoadingAllPaychecks &&
                            (detectedPaychecks ?? []).length < 1 && (
                                <PaycheckRoutingSetupWidget />
                            )}
                    </YStack>
                    <ProductFeatureDisclosures
                        activeDisclosures={
                            showFruitfulCardFeatureCard ? ['CARD'] : []
                        }
                    />
                </YStack>
                <MoreButtonsSheet
                    isOpen={isMoreButtonsSheetOpen}
                    onClose={() => {
                        setIsMoreButtonsSheetOpen(false);
                    }}
                    incomeSplitIsActive={incomeSplitIsActive}
                    showPaycheckSplit={showPaycheckSplit}
                    showIncomeSplit={shouldSeeIncomeSplit ?? false}
                    onSubmitPaycheckRouting={() => {
                        if (existingPaycheckRoutingConfigs.length) {
                            router.navigate(
                                '/banking/paycheck-routing/account-detection',
                            );
                        } else {
                            router.navigate('/banking/paycheck-routing/info');
                        }
                    }}
                    onSubmitIncomeSplit={routeToIncomeSplit}
                    onSubmitDirectDepositSwitch={() => {
                        setAccountForDirectDeposit(null);
                        router.navigate({
                            pathname: '/banking/direct-deposit/info',
                            params: {
                                context: 'banking',
                                source: 'banking-overview',
                            },
                        });
                    }}
                    onSubmitBeneficiaries={() => {
                        setAccountForDirectDeposit(null);
                        router.navigate({
                            pathname: '/banking/beneficiaries',
                        });
                    }}
                />
                <AvailableBalanceInfoSheet
                    totalCurrentBalance={totalCurrentBalance}
                    totalAvailableBalance={totalAvailableBalance}
                    totalCardBalance={invertNormalizedValue(totalCardBalance)}
                    totalPendingTransfers={invertNormalizedValue(
                        totalPendingTransfers,
                    )}
                    allAccountsOverview
                    totalPendingCardTransactions={totalPendingCardTransactions}
                    isOpen={isAvailableBalanceInfoSheetOpen}
                    onClose={() => {
                        setIsAvailableBalanceInfoSheetOpen(false);
                    }}
                    hasCards={hasCards}
                />
                <AccountLimitSheet
                    isOpen={isAccountLimitSheetOpen}
                    onClose={() => {
                        setIsAccountLimitSheetOpen(false);
                    }}
                />
            </YStack>
        </SmoothieScrollView>
    );
};

interface MoreButtonsSheetProps {
    isOpen: boolean;
    onClose: () => void;
    incomeSplitIsActive?: boolean;
    showPaycheckSplit: boolean;
    showIncomeSplit: boolean;
    onSubmitPaycheckRouting: () => void;
    onSubmitIncomeSplit: () => void;
    onSubmitDirectDepositSwitch: () => void;
    onSubmitBeneficiaries: () => void;
}

const MoreButtonsSheet = ({
    isOpen,
    onClose,
    incomeSplitIsActive = false,
    showPaycheckSplit,
    showIncomeSplit,
    onSubmitPaycheckRouting,
    onSubmitIncomeSplit,
    onSubmitDirectDepositSwitch,
    onSubmitBeneficiaries,
}: MoreButtonsSheetProps) => {
    const { trackViewOnce } = useViewEvents();
    const { value: enableMobileCheckDeposit } = useFeatureGate(
        FeatureFlags.ENABLE_MOBILE_CHECK_DEPOSIT,
    );
    const tabExperience = useRoadmapTabExperience();

    useEffect(() => {
        if (isOpen) {
            trackViewOnce('Banking/SpacesOverview/MoreButtonsSheet');
        }
    }, [isOpen]);

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="More options"
            showCloseButton
        >
            <YStack gap={14} marginTop={24} padding={4}>
                {enableMobileCheckDeposit && (
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.LARGE}
                        text="Deposit check"
                        icon={
                            <Icon
                                icon={ScanLineIcon}
                                size={18}
                                color="$gray1200"
                            />
                        }
                        onPress={() => {
                            onClose();
                            router.navigate('/banking/check-deposit');
                        }}
                    />
                )}
                <Button
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.LARGE}
                    text="Recurring transfers"
                    icon={
                        <Icon
                            icon={RefreshCwIcon}
                            size={18}
                            color="$gray1200"
                        />
                    }
                    onPress={() => {
                        onClose();
                        router.navigate('/banking/manage-recurring-transfers');
                    }}
                />
                {showPaycheckSplit && (
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.LARGE}
                        text="Paycheck split"
                        icon={
                            <View rotate="180deg">
                                <Icon
                                    icon={SplitIcon}
                                    size={18}
                                    color="$gray1200"
                                />
                            </View>
                        }
                        onPress={() => {
                            onClose();
                            onSubmitPaycheckRouting();
                        }}
                    />
                )}
                {showIncomeSplit &&
                    tabExperience !== 'money_system' &&
                    tabExperience !== 'loading' && (
                        <IncomeSplitButton
                            onClose={onClose}
                            onSubmitIncomeSplit={onSubmitIncomeSplit}
                            incomeSplitIsActive={incomeSplitIsActive}
                        />
                    )}
                <Button
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.LARGE}
                    text="Statements and docs"
                    icon={
                        <Icon icon={FileTextIcon} size={18} color="$gray1200" />
                    }
                    onPress={() => {
                        onClose();
                        router.navigate('/banking/documents');
                    }}
                />
                <Button
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.LARGE}
                    text="Set up direct deposit"
                    icon={
                        <Icon
                            icon={BanknoteArrowDownIcon}
                            size={18}
                            color="$gray1200"
                        />
                    }
                    onPress={() => {
                        onClose();
                        onSubmitDirectDepositSwitch();
                    }}
                />
                <Button
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.LARGE}
                    text="Beneficiaries"
                    icon={<Icon icon={IdCard} size={18} color="$gray1200" />}
                    onPress={() => {
                        onClose();
                        onSubmitBeneficiaries();
                    }}
                />
            </YStack>
        </Sheet>
    );
};

export default SpacesOverview;
